import { withApollo, WithApolloClient } from '@apollo/client/react/hoc';
import { Align, Bar, Button, Divider, Icon, Position, Row, Size, Tooltip } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import _ from 'lodash';
import * as React from 'react';
import { Component, ReactNode } from 'react';
import SearchType from 'types/enum/SearchType';
import { logError } from 'utils/testUtils';
import { LazyLoadPickerList, LazyLoadSearchablePickerList } from '../../..';
import { ArrowBackIcon, ChevronRightIcon } from '../../../../assets/icons';
import IProject from '../../../../types/Project';
import { projectUtils, stringUtils } from '../../../../utils';
import Picker from '../../../foundation/pickers/Picker/Picker';
import { projectQuery, projectsPickerQuery, subProjectQuery, subProjectSearchQuery } from './project-picker-queries';
import './ProjectPicker.scss';

export interface IProjectPickerProps {
  value: string | null;
  error?: boolean;
  onSelect: (projectId: string | null) => void;
  placeholder?: string;
  className?: string | object;
  position?: Position;
  client?: any;
  dropDownButtonRender?: ReactNode; // Drop down button on the picker
  closeButtonRender?: (handleClear: (event: React.KeyboardEvent | React.MouseEvent) => void) => ReactNode; // Close button render on picker
  fetchPolicy?: string;
  searchArchivedProjects?: boolean;
  topLevelOnly?: boolean;
  onChange?: (value: string | null) => void;
}

interface IState {
  project: IProject | null;
  searchValue: string;
  layers: IProject[];
  projectData: IProject[];
  subProjectData: IProject[];
  projectError: boolean;
  loadedAllProjects: boolean;
  loadedAllSubProjects: boolean;
  focusStartId?: string | null;
}

export const PROJECT_PICKER_SECTION_SIZE = 500;

class ProjectPicker extends Component<WithApolloClient<IProjectPickerProps>, IState> {
  public state: IState = {
    focusStartId: null,
    layers: [],
    loadedAllProjects: false,
    loadedAllSubProjects: false,
    project: null, // The project object of the picker's value (projectId)
    projectData: [],
    projectError: false,
    searchValue: '',
    subProjectData: [],
  };
  public sectionSize = PROJECT_PICKER_SECTION_SIZE;
  public tooltipDelay = 750;
  public dividerIndex?: number | null;
  public picker?: HTMLElement; // ref
  public searchDebounce?: () => void; // used for debouncing api calls on search

  public componentDidMount() {
    this.setProject();
  }

  public componentDidUpdate(prevProps: IProjectPickerProps, prevState: IState) {
    if (prevState.searchValue !== this.state.searchValue) {
      // Load search results if searchValue has changed
      if (!this.searchDebounce) {
        this.searchDebounce = this.searchDebounce || _.debounce(this.clearProjectData, 200);
      }
      this.searchDebounce();
    }

    if (prevProps.value !== this.props.value) {
      this.setProject();
    }

    if (prevState.layers !== this.state.layers) {
      this.setState({
        loadedAllProjects: false,
        loadedAllSubProjects: false,
        projectData: [],
        subProjectData: [],
      });
    }

    if (prevProps.searchArchivedProjects !== this.props.searchArchivedProjects) {
      this.setState({
        loadedAllProjects: false,
        loadedAllSubProjects: false,
        projectData: [],
        subProjectData: [],
      });
    }
  }

  public clearProjectData() {
    this.setState({
      loadedAllProjects: false,
      projectData: [],
    });
  }

  // Load the search screen
  public loadSearchSheet = (after?: string): Promise<IProject[]> => {
    const projectsCall = new Promise((resolve, reject) => {
      const variables: any = {
        after,
        first: this.sectionSize,
        filter: {
          depth: { equal: 1 },
          archivedOn: this.props.searchArchivedProjects ? { isNull: false } : { isNull: true },
        },
      };

      if (this.state.searchValue) {
        variables.filter.search = { type: SearchType.CONTAINS, value: this.state.searchValue, fields: ['title'] };
      }
      return this.props.client
        .query({
          query: projectsPickerQuery,
          fetchPolicy: this.props.fetchPolicy,
          variables,
        })
        .then((result: object) => resolve(result))
        .catch((error: Error) => {
          logError(error);
          return reject(error);
        });
    });

    const subprojectsCall = new Promise((resolve, reject) => {
      const variables: any = {
        first: this.sectionSize,
        filter: {
          depth: { greaterThan: 1 },
          archivedOn: this.props.searchArchivedProjects ? { isNull: false } : { isNull: true },
        },
      };

      if (this.state.searchValue) {
        variables.filter.search = { type: SearchType.CONTAINS, value: this.state.searchValue, fields: ['title'] };
      }

      return this.props.client // Query for all subprojects that match the current search string
        .query({
          query: subProjectSearchQuery,
          variables,
        })
        .then((result: object) => resolve(result))
        .catch((error: Error) => {
          logError(error);
          return reject(error);
        });
    });

    let queries: Array<Promise<any>> = [];

    if (this.props.topLevelOnly || !this.state.searchValue) {
      queries = [projectsCall];
    } else {
      queries = [projectsCall, subprojectsCall];
    }

    return new Promise((resolve, reject) => {
      return Promise.all(queries)
        .then((results: any) => {
          const results1 = results[0].data.projects;
          const results2 = results.length > 1 ? results[1].data.projects : [];
          this.dividerIndex = results1.length && results2.length ? results1.length - 1 : null;

          resolve([...results1, ...results2]);
        })
        .catch((error: Error) => {
          logError(error);
          return reject(error);
        });
    });
  };

  public loadSubProjectSheet = (after?: string): Promise<IProject[]> => {
    return new Promise((resolve, reject) => {
      return this.props.client
        .query({
          query: subProjectQuery,
          variables: {
            after,
            first: this.sectionSize,
            projectId: this.state.layers[this.state.layers.length - 1].id,
          },
        })
        .then((result: object) => {
          const subprojects = this.getSubProjectsFromApolloData(result);

          resolve(
            subprojects.filter((item: IProject) => {
              return item.archivedOn === null;
            })
          );
        })
        .catch((error: Error) => {
          logError(error);
          return reject(error);
        });
    });
  };

  public setProject = () => {
    const { value } = this.props;
    if (value) {
      return this.props.client
        .query({
          query: projectQuery,
          fetchPolicy: this.props.fetchPolicy || 'cache-first',
          variables: {
            filter: {
              id: { equal: value },
            },
          },
        })
        .then((result: object) => {
          const project = _.get(result, 'data.projects[0]');
          if (project) {
            this.setState({
              focusStartId: project.id,
              project,
            });
          }
        })
        .catch((error: Error) => {
          logError(error);
          return Promise.reject(error);
        });
    } else {
      this.setState({
        focusStartId: null,
        project: null,
      });
    }
  };

  public renderRow = (row: IProject, index: number) => {
    if (
      this.state.searchValue &&
      row.ancestors &&
      row.ancestors.length &&
      !this.state.layers.length // only show on searchable list
    ) {
      return this.renderSubSearchRow(row);
    } else {
      return this.renderBasicRow(row, index);
    }
  };

  public hasActiveSubProjects = (row: IProject) => {
    return (
      row.children &&
      row.children.length > 0 &&
      !this.props.topLevelOnly &&
      row.children.some((item: IProject) => {
        return item.archivedOn === null;
      })
    );
  };

  // Row without project path
  public renderBasicRow = (row: IProject, index: number) => {
    const handleChevronClick = (e: React.MouseEvent) => {
      this.handleChevronClick(row, e);
    };

    return (
      <>
        <Bar size={Size.SMALL} className="px-3">
          <div className="picker-row-title ellipsis">{stringUtils.highlight(row.title, this.state.searchValue)}</div>
          {this.hasActiveSubProjects(row) && (
            <div className="subproject-btn">
              <Button type="icon" onClick={handleChevronClick}>
                <Icon svg={ChevronRightIcon} />
              </Button>
            </div>
          )}
        </Bar>
        {this.state.layers.length === 0 && this.dividerIndex === index && <Divider className="my-3" />}
      </>
    );
  };

  // Render row with project path
  public renderSubSearchRow = (project: IProject) => {
    const handleChevronClick = (e: React.MouseEvent) => {
      this.handleChevronClick(project, e);
    };

    return (
      <Bar key={project.id} className="px-3 subproject-search-row" size={Size.LARGE}>
        <div>
          <div className="picker-row-title ellipsis">
            {stringUtils.highlight(project.title, this.state.searchValue)}
          </div>
          <div className="f-sm fc-2 ellipsis">{projectUtils.getFormattedPathFromProject(project)}</div>
        </div>
        {this.hasActiveSubProjects(project) && (
          <div className="subproject-btn">
            <Button type="icon" ripple={false} onClick={handleChevronClick}>
              <Icon svg={ChevronRightIcon} />
            </Button>
          </div>
        )}
      </Bar>
    );
  };

  // Navigate to subprojects sheet
  public drillIn = (project: IProject, _e: React.KeyboardEvent | React.MouseEvent) => {
    if (project && project.children && project.children.length) {
      setTimeout(() => {
        this.setState((prevState: IState) => {
          return {
            focusStartId: null,
            layers: [...prevState.layers, project],
            loadedAllProjects: false,
            loadedAllSubProjects: false,
            projectData: [],
            subProjectData: [],
          };
        });
      });
    }
  };

  // Navigate back one level
  public goBack = () => {
    const layers = [...this.state.layers];
    const pop = layers.pop();

    if (pop) {
      setTimeout(() => {
        this.setState({
          focusStartId: pop.id,
          layers,
          loadedAllProjects: false,
          loadedAllSubProjects: false,
          projectData: [],
          subProjectData: [],
        });
      });
    }
  };

  public handleClear = () => {
    this.setState({
      focusStartId: null,
      layers: [],
      searchValue: '',
    });
  };

  public handleOnOpen = () => {
    const { project } = this.state;

    if (project) {
      this.setState(
        {
          // configure the picker using the existing selected project
          layers: _.sortBy(project.ancestors, 'depth'),
          searchValue: '',
        },
        () => {
          if (project.ancestors && project.ancestors.length) {
            this.loadSubProjectSheet();
          } else {
            this.loadSearchSheet();
          }
        }
      );
    } else {
      this.setState(
        {
          // Reset search and what screen is showing
          layers: [],
          searchValue: '',
        },
        () => {
          this.loadSearchSheet();
        }
      );
    }
  };

  public handleSearchInputChange = (value: string) => {
    if (value !== this.state.searchValue) {
      this.setState({ searchValue: value });
    }
  };

  public handleSelect = (project: IProject | null) => {
    this.props.onSelect(project ? project.id : null);

    setTimeout(() => {
      if (this.picker) {
        this.picker.focus();
      }
    });
  };

  public handleChevronClick = (project: IProject, e: React.KeyboardEvent | React.MouseEvent) => {
    e.stopPropagation();
    this.drillIn(project, e);
  };

  public buildValueTemplate = () => {
    const { project } = this.state;

    if (project) {
      const label = projectUtils.getFormattedPathFromProject(project, true);

      if (label) {
        return (
          <Tooltip label={label} delay={this.tooltipDelay}>
            <span>{label}</span>
          </Tooltip>
        );
      }
    }

    return <></>;
  };

  // Render the header for non-searchable lists
  public renderSecondaryHeader = () => (
    <Row className="picker-header px-3" align={Align.CENTER}>
      <Button size={Size.SMALL} type="icon" onClick={this.goBack} className="mr-2">
        <Icon svg={ArrowBackIcon} />
      </Button>
      <div>
        <h5 className="picker-title fw-bold">{this.state.layers[this.state.layers.length - 1].title}</h5>
        <div className="f-sm fc-2">
          {this.state.layers.map((layer, index) => (
            <React.Fragment key={index}>
              {index !== this.state.layers.length - 1 && (
                <span>
                  {layer.title} {index !== this.state.layers.length - 2 && '/'}{' '}
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </Row>
  );

  public setPickerRef = (el: HTMLElement) => {
    this.picker = el;
  };

  public getSubProjectsFromApolloData = (result: any) => {
    const data = result.data;
    if (data && data.projects && data.projects.length) {
      const subprojects = data.projects[0].children;
      return [...subprojects].sort((a: IProject, b: IProject) => a.title.localeCompare(b.title));
    }

    return [];
  };

  public handleSearchSheetDidLoad = (items: IProject[], error: boolean, loadedAll: boolean) => {
    if (!error) {
      this.setState({
        loadedAllProjects: this.state.searchValue ? true : loadedAll,
        projectData: items,
      });
    } else {
      this.setState({
        loadedAllProjects: true, // remove spinner
      });
    }
  };

  public handleSubProjectSheetDidLoad = (items: IProject[], error: boolean) => {
    if (!error) {
      this.setState({
        loadedAllSubProjects: true,
        subProjectData: items,
      });
    } else {
      this.setState({
        loadedAllSubProjects: true, // remove spinner
      });
    }
  };

  public renderPickerLists = (close: () => void) => {
    const handleCloseMenu = (shouldFocus: boolean) => {
      if (shouldFocus && this.picker) {
        this.picker.focus();
      }
      close();
    };

    return (
      <>
        {this.state.layers.length === 0 && (
          <LazyLoadSearchablePickerList
            getData={this.loadSearchSheet}
            didLoad={this.handleSearchSheetDidLoad}
            value={this.state.focusStartId}
            renderRow={this.renderRow}
            data={this.state.projectData}
            loadedAll={this.state.loadedAllProjects}
            closeMenu={handleCloseMenu}
            onSelect={this.handleSelect}
            searchValue={this.state.searchValue}
            onSearch={this.handleSearchInputChange}
            onRightKeyDown={this.handleChevronClick}
          />
        )}
        {this.state.layers.length > 0 && (
          <LazyLoadPickerList
            getData={this.loadSubProjectSheet}
            didLoad={this.handleSubProjectSheetDidLoad}
            value={this.state.focusStartId}
            loadedAll={this.state.loadedAllSubProjects}
            renderRow={this.renderRow}
            data={this.state.subProjectData}
            closeMenu={handleCloseMenu}
            onSelect={this.handleSelect}
            onRightKeyDown={this.drillIn}
            onLeftKeyDown={this.goBack}
            header={this.renderSecondaryHeader}
          />
        )}
      </>
    );
  };

  public render() {
    const { value, placeholder, className, position, error, dropDownButtonRender, closeButtonRender, onChange } =
      this.props;

    const classes = classNames(
      {
        'project-picker': true,
      },
      className
    );

    return (
      <Picker
        value={value}
        onSelect={this.handleSelect}
        onClear={this.handleClear}
        className={classes}
        error={error}
        dropDownButtonRender={dropDownButtonRender}
        closeButtonRender={closeButtonRender}
        position={position}
        placeholder={placeholder}
        valueTemplate={this.buildValueTemplate}
        onOpen={this.handleOnOpen}
        forwardRef={this.setPickerRef}
        minWidth="350px"
        contentTemplate={this.renderPickerLists}
        onChange={onChange}
      />
    );
  }
}

// TODO: Add archived filtering
// TODO: Implement lazy loading once server-side paging is available

export default withApollo<IProjectPickerProps>(ProjectPicker);
